// The browser half of TWT's own sign-in code (src/pages/api/login/code/*):
// the requests, turning the answered Clerk ticket into the TWT session, and
// the same-browser hand-off between the tab waiting on the code and the tab
// the email's Sign in button opened. Nothing here loads Clerk; surfaces pass
// in what they have (ClerkTicketSession).

import {
  completeOutcome,
  type CompleteResponse,
} from "~/components/auth/signInPanelFlow";
import { track } from "~/lib/analytics/track";

/** What the sign-in needs from Clerk. Surfaces pass null while Clerk loads. */
export type ClerkTicketSession = {
  /** The Clerk user this browser is already signed in as, if any. */
  signedInUserId: string | null;
  signedInEmail: string | null;
  signInWithTicket(ticket: string): Promise<void>;
  signOut(): Promise<void>;
};

/** A verified code or a tapped link, ready to become a session. */
export type CodeSignIn = {
  ticket: string;
  clerkUserId: string;
  next: string;
  handoff: string;
};

export type LoginCodeFailure = {
  ok: false;
  code?: string;
  message?: string;
  email?: string;
  next?: string;
};

async function postJson<T>(
  url: string,
  body: unknown
): Promise<{
  status: number;
  body: T | LoginCodeFailure;
}> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({ ok: false }))) as
    | T
    | LoginCodeFailure;
  return { status: res.status, body: json };
}

export function requestLoginCode(input: { email: string; next: string }) {
  return postJson<{
    ok: true;
    flowToken: string;
    email: string;
    handoff: string;
    expiresAt: string;
  }>("/api/login/code/request", input);
}

export function verifyLoginCode(input: { flowToken: string; code: string }) {
  return postJson<{ ok: true } & CodeSignIn>("/api/login/code/verify", input);
}

/** Whether the email's button used this flow, and this browser is now signed in. */
export function checkLoginCode(flowToken: string) {
  return postJson<{ ok: true; used: boolean; signedIn: boolean }>(
    "/api/login/code/verify",
    { flowToken, check: true }
  );
}

/** The link page on load: the masked address, and whether the link still works. */
export function checkLoginLink(token: string) {
  return postJson<{ ok: true; email: string; next: string }>(
    "/api/login/code/link",
    { token, check: true }
  );
}

/** The link page after the tap: spends the flow and answers the ticket. */
export function redeemLoginLink(token: string) {
  return postJson<{ ok: true; email: string } & CodeSignIn>(
    "/api/login/code/link",
    { token }
  );
}

export type CodeSignInOutcome =
  | { kind: "signed_in"; redirectTo: string }
  | { kind: "switch_account"; signedInEmail: string | null }
  | {
      kind: "error";
      message: string;
      /** "complete": only the TWT step failed and can run again.
       * "new_code": the code is spent, so the member needs a new one. */
      retry: "complete" | "new_code" | null;
    };

/** POST /api/login/clerk/complete: bind the member and set the TWT session. */
export async function completeCodeSignIn(
  signIn: Pick<CodeSignIn, "next" | "clerkUserId">
): Promise<CodeSignInOutcome> {
  try {
    const res = await fetch("/api/login/clerk/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        method: "email_code",
        next: signIn.next,
        expectedClerkUserId: signIn.clerkUserId,
      }),
    });
    const body = (await res
      .json()
      .catch(() => ({}))) as CompleteResponse["body"];
    const outcome = completeOutcome({ status: res.status, body });
    if (outcome.kind === "navigate") {
      track("sign_in_finished", { method: "email_code" });
      return { kind: "signed_in", redirectTo: outcome.href };
    }
    return {
      kind: "error",
      message: outcome.message,
      retry:
        body.code === "clerk_user_mismatch"
          ? "new_code"
          : outcome.recoverable
          ? "complete"
          : null,
    };
  } catch {
    return {
      kind: "error",
      message:
        "Your sign-in was accepted, but we couldn't finish connecting to TWT. Try again.",
      retry: "complete",
    };
  }
}

/**
 * Turns a verified code or a tapped link into the TWT session. The browser
 * signs in to Clerk with the ticket, unless it already holds this user's
 * Clerk session, then /api/login/clerk/complete sets the TWT session. A Clerk
 * session for someone else is signed out only after the member confirmed the
 * switch (`switchAccount`); until then the answer is "switch_account".
 */
export async function finishCodeSignIn(input: {
  clerk: ClerkTicketSession;
  signIn: CodeSignIn;
  switchAccount?: boolean;
}): Promise<CodeSignInOutcome> {
  const { clerk, signIn } = input;
  if (clerk.signedInUserId !== signIn.clerkUserId) {
    if (clerk.signedInUserId && !input.switchAccount) {
      return { kind: "switch_account", signedInEmail: clerk.signedInEmail };
    }
    try {
      if (clerk.signedInUserId) await clerk.signOut();
      await clerk.signInWithTicket(signIn.ticket);
    } catch {
      return {
        kind: "error",
        message:
          "We couldn't finish signing you in. Request a new code and try again.",
        retry: "new_code",
      };
    }
  }
  return completeCodeSignIn(signIn);
}

/** Waits for Clerk to load, up to `timeoutMs`; null when it never did. */
export async function waitForClerk(
  read: () => ClerkTicketSession | null,
  timeoutMs = 15_000
): Promise<ClerkTicketSession | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const clerk = read();
    if (clerk) return clerk;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

// Same-browser hand-off. A tab on the code step listens; the link page, once
// signed in, says so and waits a moment for an answer. With an answer the
// link page tells the member they can close it and the waiting tab moves on;
// with none (another browser or device) the link page moves on itself.
const SIGN_IN_CHANNEL = "twt-sign-in";
const SIGNED_IN = "twt-code-signed-in";
const SIGNED_IN_ANSWER = "twt-code-signed-in-answer";

function openSignInChannel(): BroadcastChannel | null {
  if (
    typeof window === "undefined" ||
    typeof window.BroadcastChannel !== "function"
  ) {
    return null;
  }
  try {
    return new window.BroadcastChannel(SIGN_IN_CHANNEL);
  } catch {
    return null;
  }
}

/** The link page: true when a tab waiting on this code answered. */
export function announceCodeSignIn(input: {
  handoff: string;
  redirectTo: string;
  waitMs?: number;
}): Promise<boolean> {
  const channel = openSignInChannel();
  if (!channel) return Promise.resolve(false);
  return new Promise((resolve) => {
    const finish = (answered: boolean) => {
      window.clearTimeout(timer);
      channel.close();
      resolve(answered);
    };
    const timer = window.setTimeout(() => finish(false), input.waitMs ?? 800);
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; handoff?: unknown } | null;
      if (data?.type === SIGNED_IN_ANSWER && data.handoff === input.handoff) {
        finish(true);
      }
    };
    channel.postMessage({
      type: SIGNED_IN,
      handoff: input.handoff,
      redirectTo: input.redirectTo,
    });
  });
}

/** The code step: answers the link page for one of its own codes. */
export function listenForCodeSignIn(input: {
  handoffs: () => ReadonlySet<string>;
  onSignedIn: (redirectTo: string) => void;
}): () => void {
  const channel = openSignInChannel();
  if (!channel) return () => undefined;
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data as {
      type?: unknown;
      handoff?: unknown;
      redirectTo?: unknown;
    } | null;
    if (
      data?.type !== SIGNED_IN ||
      typeof data.handoff !== "string" ||
      !input.handoffs().has(data.handoff)
    ) {
      return;
    }
    channel.postMessage({ type: SIGNED_IN_ANSWER, handoff: data.handoff });
    input.onSignedIn(
      typeof data.redirectTo === "string" ? data.redirectTo : ""
    );
  };
  return () => channel.close();
}

export function maskEmailForDisplay(email: string) {
  const [local = "", domain = ""] = email.trim().split("@");
  if (!local || !domain) return "";
  const shown = local.slice(0, Math.min(5, Math.max(1, local.length - 1)));
  return `${shown}***@${domain}`;
}
